import {Component, Input, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {StateService, Transition} from '@uirouter/angular';
import { CplTag } from 'src/app/model/cpl-tag.model';
import { ApplicationRoles, PagedResponse, PortalSettings, SessionService, User } from '@edl-development/edl-common';
import { CplUserRequestSummary } from 'src/app/model/request/cpl-user-request-summary.model';
import { AupRequestItemStatus } from '../../enums/aup-request-item-status.enum';
import { RequestTypeFilter } from '../../enums/request-type-filter.enum';
import { EdlSelectOption } from 'src/app/model/common/edl-select-option.model';
import { REQUESTS_TAB_SUPERVISOR_STATUS_FILTERS, REQUESTS_TAB_MY_ITEMS_STATUS_FILTERS } from 'src/app/constants/roster.constants';

const DEFAULT_STATUS = AupRequestItemStatus.Active;

@Component({
    selector: 'edl-requests-tab',
    templateUrl: './requests-tab.component.html',
    styleUrls: ['./requests-tab.component.scss']
})
export class RequestsTabComponent implements OnInit {
    @Input() pagedResponse: PagedResponse<CplUserRequestSummary>;
    @Input() tags: CplTag[];
    @Input() user: User;
    @Input() isMyItems: boolean;

    form: UntypedFormGroup;
    portalSettings: PortalSettings;
    ApplicationRoles = ApplicationRoles;

    isUser = false;
    filterStatuses: EdlSelectOption<AupRequestItemStatus>[];

    AupRequestItemStatus = AupRequestItemStatus;
    RequestTypeFilter = RequestTypeFilter;

    // Tracks last interaction to prevent change event conflicts between mouse and keyboard
    private lastSelectInteraction: 'mouse' | null = null;

    constructor(
        private sessionService: SessionService,
        private formBuilder: UntypedFormBuilder,
        private transition: Transition,
        private stateService: StateService
    ) { }

    /**
     * Get the storage key for this user's filters
     * Makes filters user-specific so switching between users doesn't share filters
     */
    private getFilterStorageKey(): string {
        const userId = this.user?.UserID || 'unknown';
        return `aup_requests_tab_${userId}`;
    }

    ngOnInit() {
        const urlParams = this.transition.params();
        this.portalSettings = this.sessionService.PortalSettings;
        this.isUser = this.user?.UserID === this.sessionService.user?.UserID;

        this.filterStatuses = this.isMyItems
            ? REQUESTS_TAB_MY_ITEMS_STATUS_FILTERS
            : REQUESTS_TAB_SUPERVISOR_STATUS_FILTERS;

        // Load saved filters from SessionService
        const savedFilters = this.sessionService.GetSessionValue(this.getFilterStorageKey()) as any;

        // Priority: URL params > Saved filters > Defaults
        const searchBy = urlParams.searchBy ?? savedFilters?.searchBy ?? '';
        const status = urlParams.status ?? savedFilters?.status ?? DEFAULT_STATUS;
        const tag = urlParams.tag ?? savedFilters?.tag ?? 0;
        const requestType = urlParams.requestType ?? savedFilters?.requestType ?? RequestTypeFilter.MyRequests;

        this.form = this.formBuilder.group({
            searchBy: [searchBy, []],
            filterStatusBy: [status, []],
            filterTagBy: [Number(tag), []],
            requestType: [requestType, []]
        });
        this.ApplicationRoles = ApplicationRoles;

        // Save filters whenever form changes
        this.form.valueChanges.subscribe(() => {
            this.saveCurrentFilters();
        });
    }
    
    private saveCurrentFilters(): void {
        const filters = {
            searchBy: this.form.value.searchBy,
            status: this.form.value.filterStatusBy,
            tag: this.form.value.filterTagBy,
            requestType: this.form.value.requestType
        };
        this.sessionService.SetSessionValue(this.getFilterStorageKey(), filters);
    }

    search(form: UntypedFormGroup) {
        this.saveCurrentFilters();

        this.stateService
            .go('.', {
                page: 1,
                searchBy: form.value.searchBy,
                status: form.value.filterStatusBy,
                tag: form.value.filterTagBy,
                requestType: form.value.requestType
            });
    }

    showSubmissions(request: CplUserRequestSummary) {
        const { requestType } = this.transition.params();
        this.stateService.go('^.submissions', {
            requestId: request.RequestItem.RequestID,
            requestType: requestType || RequestTypeFilter.MyRequests,
            page: 1
        });
    }

    onRequestTypeChange(event: any) {
        this.search(this.form);
    }

    onTagChange(event: any) {
        // Keyboard Enter is handled in keydown handler to avoid firing on Tab blurs.
        if (this.lastSelectInteraction === 'mouse') {
            this.search(this.form);
        }
        this.lastSelectInteraction = null;
    };

    onStatusChange(event: any) {
        if (this.lastSelectInteraction === 'mouse') {
            this.search(this.form);
        }
        this.lastSelectInteraction = null;
    };


    clearFiltersAndRefresh() {
        this.sessionService.SetSessionValue(this.getFilterStorageKey(), null);

        this.form.reset({
            searchBy: '',
            filterStatusBy: DEFAULT_STATUS,
            filterTagBy: 0,
            requestType: RequestTypeFilter.MyRequests
        });

        this.search(this.form);
    }

    showReturnToAllUsersProgress() {
        const currentUserId = this.sessionService.user?.UserID;
        const paramUserId = this.transition.params().userId;

        return ((paramUserId !== null && paramUserId !== undefined) && (currentUserId !== paramUserId));
    }

    clickReturnToAllUsersProgress() {
        this.stateService.go('authenticated.all-users-progress.user-compliance.view', {
            page: 1
        }, { reload: true });
    }

    public get canViewRequestType() {
        const userRole = this.user?.AccessRoleID;
        return userRole === this.ApplicationRoles.ADMIN ||userRole === this.ApplicationRoles.EDLADMIN || userRole === this.ApplicationRoles.SUPERVISOR || userRole === this.ApplicationRoles.LIMITED_SUPERVISOR;
    }

    //#region Keyboard Helpers
    
    handleSearchKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.search(this.form);
        }
    }

    handleStatusKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.search(this.form);
        }
    }

    handleTagKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.search(this.form);
        }
    }

    onSelectMouseDown() {
        this.lastSelectInteraction = 'mouse';
    }

    //#endregion
}